import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsIconGlyph } from '@/ds/icon'
import { DsIcons, type DsIcon } from '@/ds/icons'
import { DsButton } from '@/ds/button'

export interface DsAlertProps {
  title?: string
  description?: string
  tone?: DsTone
  icon?: DsIcon
  actionLabel?: string
  onAction?: () => void
  /** Shows the action as busy and ignores clicks, e.g. while it opens something. */
  actionLoading?: boolean
  onDismiss?: () => void
  dismissLabel?: string
  bleed?: boolean
  className?: string
}

const toneIcon: Record<DsTone, DsIcon> = { neutral: DsIcons.info, brand: DsIcons.info, success: DsIcons.success, warning: DsIcons.warning, danger: DsIcons.error, info: DsIcons.info }

/** Live region for warning/danger. */
export function DsAlert({ title, description, tone = 'info', icon, actionLabel, onAction, actionLoading, onDismiss, dismissLabel = 'Dismiss', bleed, className }: DsAlertProps) {
  const t = toneClass[tone]
  const live = tone === 'danger' || tone === 'warning'
  return (
    <div role={live ? 'alert' : 'status'} className={cn('flex items-start gap-3 border p-3', t.soft, t.border, bleed ? 'rounded-none border-x-0' : 'rounded-lg', className)}>
      <DsIconGlyph icon={icon ?? toneIcon[tone]} size={20} className={cn('mt-0.5', t.onSoft)} />
      <div className="min-w-0 flex-1">
        {title && <p className={cn('text-small-strong', t.onSoft)}>{title}</p>}
        {description && <p className={cn('text-small', t.onSoft, title && 'opacity-90')}>{description}</p>}
        {actionLabel && (
          <DsButton variant="link" size="sm" label={actionLabel} onClick={onAction} loading={actionLoading} semanticLabel={actionLabel} className={cn('mt-1 !px-0', t.onSoft)} />
        )}
      </div>
      {onDismiss && (
        <button type="button" aria-label={dismissLabel} onClick={onDismiss} className={cn('-m-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10', t.onSoft)}>
          <DsIconGlyph icon={DsIcons.close} size={16} />
        </button>
      )}
    </div>
  )
}

export const DsBanner = (props: Omit<DsAlertProps, 'bleed'>) => <DsAlert bleed {...props} />
